import React, { FC, ReactNode, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { QueryResult } from 'react-apollo';

import { FeedByUser, UpdateUserProfileDocument, UserProfile } from 'api';
import { getFullName, getSubdomainUrl } from 'helpers';
import Cookies from 'cookies';
import { isRoot } from 'utilities';
import {
  withApolloClient,
  ClientContext,
  FeaturesProvider,
  FollowedProvider,
  FollowersProvider,
  NotifyProvider,
  RefetcherProvider,
  UserContext,
  IWithApolloClient,
} from 'providers';

const { REACT_APP_INTERCOM_APP_ID } = process.env;

interface IProps extends IWithApolloClient {
  queryResult: QueryResult;
  children: ReactNode;
}

const Providers: FC<IProps> = ({ queryResult, apolloClient, children }) => {
  const hasMountRan = useRef(false);
  const { i18n } = useTranslation();

  const { getByUserFeed } = queryResult.data;
  const { client }: FeedByUser = getByUserFeed;
  const { user }: { user: UserProfile } = getByUserFeed;

  // Triggered after an SSO user signs in
  // IMPORTANT: Prevents rendering the app on the base domain
  if (isRoot()) {
    window.location.assign(getSubdomainUrl(client.subdomain));
    return null;
  }

  async function reconcileLanguage() {
    const { languages } = i18n;
    const isLanguageMatch = user.lang === languages[0];

    if (isLanguageMatch) {
      return;
    }

    await apolloClient.mutate({
      mutation: UpdateUserProfileDocument,
      variables: { data: { id: user.id, lang: languages[0] } },
    });
  }

  function setupIntercom() {
    window.Intercom &&
      window.Intercom('boot', {
        app_id: REACT_APP_INTERCOM_APP_ID,
        email: user.email_address,
        user_id: user.id,
        client_id: user.client_id,
        client_name: client.title,
        name: getFullName(user),
        job_title: user.job_title,
        department_title: user.department_title,
        location_title: user.location_title,
        created_at: user.created_at,
        german_privacy: user.german_privacy,
        timezone: user.user_timezone,
      });
  }

  function setupSegment() {
    window.analytics && window.analytics.identify(user.id);
  }

  useEffect(() => {
    if (!hasMountRan.current) {
      setupIntercom();
      setupSegment();
      reconcileLanguage();
      Cookies.clientId = client.id;
      Cookies.clientSubdomain = client.subdomain;

      hasMountRan.current = true;
    }
  }, [queryResult]);

  return (
    <ClientContext.Provider value={{ client }}>
      <UserContext.Provider value={{ user }}>
        <FeaturesProvider>
          <FollowedProvider>
            <FollowersProvider>
              <NotifyProvider>
                <RefetcherProvider>{children}</RefetcherProvider>
              </NotifyProvider>
            </FollowersProvider>
          </FollowedProvider>
        </FeaturesProvider>
      </UserContext.Provider>
    </ClientContext.Provider>
  );
};

export default withApolloClient(Providers);
